import React, { useMemo, useState, useEffect, useRef } from "react";
import { getLocale, useIntl } from "umi";
import moment from "moment";
import { Segment } from "tea-component";
import PerformanceMetricsTable from "@/pages/dashboard/components/PerformanceMetricsTable";
import DimensionOfTable, { accumulateRef } from "@/pages/dashboard/pages/funnel/components/FunnelTable/components/DimensionOfTable";
import SelectShowValueType, { ShowValueType } from "@/pages/dashboard/pages/funnel/components/FunnelTable/components/SelectShowValueType";
import {
  getTableRequestParams, useStatisticsDataFormat,
  useTableColumns,
  useTableDataFormat,
  useTableState
} from "@/pages/dashboard/pages/funnel/components/FunnelTable/hooks";
import useStepOptions from "@/pages/dashboard/pages/funnel/_config/useStepOptions";
import useFunnelOptionsByFilter from "@/pages/dashboard/pages/funnel/_config/useFunnelOptionsByFilter";
import { LANGUAGE_MAP } from "@/pages/dashboard/_config/constant";

import { getFunnelPerformanceTable, downLoadFunnelPerformanceTable } from "@/pages/dashboard/services/funnel";
import { LANGUAGE_EN, TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from "@/pages/home/_config/config";
import { downLoadApi, getApiData } from "@/pages/dashboard/services/common";
import { formatFilterValues } from "@/pages/dashboard/utils/utils";
import { useOverLimitTrigger } from "@/pages/dashboard/components/OverLimitTip";
import { selectValByCondition } from "@home/utils/common";

import "@/pages/dashboard/assets/scss/page/Overlay/components/tableConext.scss";
import './index.scss';

const getTableHeadWidth = (text: any, isEn: boolean) => {
  const upDownWidth = 75;
  const defaultWidth = 200 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
  let width = defaultWidth;
  let onlyValueWidth = defaultWidth - upDownWidth;
  // 区分中英文宽度
  if (isEn && text?.length > 10) {
    const L = text?.length - 10;
    width = defaultWidth + L * 10;
    onlyValueWidth = defaultWidth + L * 10 - upDownWidth;
  }
  if (!isEn && text?.length > 5) {
    const L = text?.length - 5;
    width = defaultWidth + L * 15;
    onlyValueWidth = defaultWidth + L * 15 - upDownWidth;
  }

  return { width, onlyValueWidth };
};

export default function Table(props: any) {
  const { pageType, filterValue, filterLoading, gameCode, explainInfo, hasAddInput,
    defaultSelect, gameConfig = {}, firstStepId, haveDate = true, dimOptions, stepOptions: propsStepOptions,
    defaultValueType = 'dod', isShowValueType = true, isShowDim = true, isShowSeg = true, copyTitleList,
    showModalData = true, api, dimensionDefaultValue = 'date', defaultSort = [], fisrtRowName, customColumnFn,
    titleEn, titleCn, isEN } = props || {};
  // 表格 state 状态
  const {
    loading, setLoading,
    isError, setIsError,
    paging, setPaging,
    sort, setSort,
    total, setTotal,
    tableData, setTableData,
    statisticsData, setStatisticsData,
  } = useTableState();
  const { formatMessage } = useIntl();
  // 下载弹窗指标options
  let stepOptions = useStepOptions(filterValue, firstStepId);
  if (propsStepOptions) {
    stepOptions = propsStepOptions;
  }
  const dimensionOptions = useFunnelOptionsByFilter(filterValue, true, defaultSelect);
  const [columns, setColumns] = useState([]);
  const [frontLogicalFlag, setFrontLogicalFlag] = useState<string | undefined>(undefined);
  const { overLimitBarTriggerKey, handleResponse: handleOverLimitResponse } = useOverLimitTrigger();

  const isEn = isEN !== undefined ? isEN : getLocale() === LANGUAGE_EN;

  const {
    DATE_COLUMN,
    getAccumulateColumns,
    getMetricColumns
  } = useTableColumns();

  const [selectType, setSelectType] = useState<ShowValueType>(defaultValueType);
  const [showStatistic] = useState(["sum", "avg"]);
  const [segValue, setSegValue] = useState('conversion');
  const stepValue = filterValue?.filterValue?.stage || [];
  const accumulateOldValueRef = useRef([dimensionDefaultValue]);
  const COLUMN_CONFIG = useMemo(() => {
    const obj: any = {};
    stepOptions?.map((item: any) => {
      if (stepValue?.includes(item?.value)) {
        const { width, onlyValueWidth } = getTableHeadWidth(item?.text, isEn);
        obj[item?.value] = {
          name: item?.text,
          width,
          onlyValueWidth,
          isPercent: segValue !== 'count',
        };
      }
    });
    return obj;
  }, [filterValue, segValue]);

  useEffect(() => {
    if (filterLoading) return;
    setPaging({ pageIndex: 1, pageSize: paging.pageSize });
    setSort(defaultSort);
    const params = getRequestParams({ pageIndex: 1, pageSize: paging.pageSize }, defaultSort, stepValue, segValue, selectType);
    getTableData(params);
  }, [filterValue, filterLoading]);

  // useEffect(() => {
  //   // 手动选择了排序(非默认排序)后，才需要重新请求接口
  //   if (sort?.length > 0 && !!sort[0]?.order) {
  //     const params = getRequestParams(paging, sort, stepValue, segValue, selectType);
  //     getTableData(params);
  //   }
  // }, [segValue]);

  const changeSegment = (value: string) => {
    // 手动选择了排序(非默认排序)后，才需要重新请求接口
    if (sort?.length > 0 && !!sort[0]?.order) {
      const params = getRequestParams(paging, sort, stepValue, value, selectType);
      getTableData(params);
    }
    setSegValue(value);
  };

  useEffect(() => {
    const dateColumn = filterValue?.timeType === "Month" ? { ...DATE_COLUMN, width: 136 } : DATE_COLUMN;
    const dateColumnArr = haveDate ? [dateColumn] : [];
    const columns = [
      ...dateColumnArr,
      ...getAccumulateColumns(accumulateRef.current?.value, haveDate, customColumnFn),
      ...getMetricColumns(stepValue, COLUMN_CONFIG, explainInfo, selectType, segValue, firstStepId)
    ];
    setColumns(columns);
  }, [accumulateRef.current?.value, selectType, filterValue, segValue, getLocale()]);

  const getTableData = (params) => {
    setIsError(false);
    setLoading(true);
    // 扩展新api接口，兼容老模式
    let getTableList = getFunnelPerformanceTable;
    let paramList = [params, LANGUAGE_MAP[getLocale()], pageType];

    if (api?.tableApi) {
      const tableApi = `${api.tableApi}?language=${LANGUAGE_MAP[getLocale()]}`;
      paramList = [tableApi, params];
      getTableList = getApiData;
    }

    getTableList(...paramList).then((resp) => {
      setLoading(false);
      setTableData(resp.metrics_list || []);
      setStatisticsData(resp.aggregation || []);
      // 设置总条数
      setTotal(resp.row_count);
      setFrontLogicalFlag(resp?.front_logical_flag);
      handleOverLimitResponse(resp?.front_logical_flag);
    }).catch(e => {
      setLoading(false);
      setIsError(true);
      console.log("overview 宽表请求错误: ", filterValue?.timeType, e);
    });
  };

  /**
   * 获取表格上传参数 
   * @param paging 页码
   * @param sort 排序
   * @param step 选择步骤值
   * @param segValue tab切换值 用于增加排序的key值后缀
   * @param selectType 展示值类选择值 用于过滤排序的key值
   * @param otherParams 其它参数
   * @returns 
   */
  const getRequestParams = (paging, sort, step, segValue, selectType, otherParams?) => {
    const { filterValue: filterVal, newFilter, diff } = filterValue || {};
    let startTime = filterVal?.rangeTime?.[0];
    let endTime = filterVal?.rangeTime?.[1];
    if (haveDate) {
      /**
           * day 少于七天补足七天
           * week 少于六周补足六周
           * month 少于六月补足六月
           */
      if (newFilter && diff) {
        switch (filterValue?.timeType) {
          case "Day": {
            startTime = moment().subtract(7, 'day').format('YYYY-MM-DD');
            break;
          }
          case "Week": {
            startTime = moment(endTime).subtract(5, 'week').format('YYYY-MM-DD');
            break;
          }
          case "Month": {
            startTime = moment(endTime).subtract(5, 'month').format('YYYY-MM-DD');
            break;
          }
        }
      }

      startTime = moment(startTime).format("YYYY-MM-DD");
      endTime = moment(endTime).format("YYYY-MM-DD");
    }


    return getTableRequestParams({
      gameCode,
      formValue: filterValue,
      period: "daily",
      accumulated: accumulateRef.current?.value,
      sort,
      paging,
      stepValue: step,
      start_time: startTime,
      end_time: endTime,
      segValue,
      selectType,
      dimensionOptions: dimOptions || dimensionOptions,
      hasAddInput,
      haveDate,
      api,
      ...otherParams,
    });
  };

  // 表格数据
  const records = [
    ...useTableDataFormat(tableData, COLUMN_CONFIG, filterValue?.timeType, segValue),
    ...useStatisticsDataFormat(statisticsData, COLUMN_CONFIG, showStatistic, segValue, fisrtRowName)
  ];

  return (
    <PerformanceMetricsTable
      title={selectValByCondition(isEn, titleEn, titleCn) || formatMessage({ id: "overview_Overall_Performance" })}
      timeType='Day'
      columns={columns}
      records={records}
      copyConfig={
        {
          copyData: records,
          copyTitleList
        }
      }
      selectType={selectType}
      sorting={{
        defaultSorts: sort,
        onSortChange(sorts) {
          const newSorts = sorts?.length === 0 ? defaultSort : sorts;
          // 排序
          setSort(newSorts);
          // 发起请求
          const params = getRequestParams(paging, newSorts, stepValue, segValue, selectType);
          getTableData(params);
        }
      }}
      paging={{
        pageIndex: paging.pageIndex,
        pageSize: paging.pageSize,
        recordCount: total,
        pageSizeOptions: [10, 20, 30, 40, 50, 100, 200],
        onPagingChange(query) {
          // 切换分页
          setPaging(query);
          // 发起请求
          const params = getRequestParams(query, sort, stepValue, segValue, selectType);
          getTableData(params);
        }
      }}
      topTip={{
        left: (
          <div className="performance-flex">
            {isShowDim && <DimensionOfTable
              ref={accumulateRef}
              filterValue={filterValue}
              gameConfig={gameConfig}
              defaultSelect={defaultSelect}
              defaultValue={dimensionDefaultValue}
              options={dimOptions}
              onClose={(accumulate) => {
                if (accumulateOldValueRef?.current?.toString() == accumulate?.toString()) {
                  return;
                }
                accumulateOldValueRef.current = accumulate;
                // 重置分页和排序 (不把重置过程写到 getRequestParams中, 是想要保证方法的独立性, 不要有多种作用混杂在一起)
                setPaging({ pageIndex: 1, pageSize: paging.pageSize });
                setSort(defaultSort);
                // 发起请求
                const params = getRequestParams({ pageIndex: 1, pageSize: paging.pageSize }, defaultSort, stepValue, segValue, selectType);
                getTableData(params);
              }}
              className={'js-overview-table-dimension performance-flex-select'}
            />}
            {isShowSeg && <Segment
              className="performance-flex-segment"
              value={segValue}
              onChange={changeSegment}
              options={[
                { text: formatMessage({ id: "funnel_conversion" }), value: 'conversion' },
                { text: formatMessage({ id: "funnel_count" }), value: 'count' },
                { text: formatMessage({ id: "funnel_lost_percent" }), value: 'lost' },
              ]}
            />}
            {isShowValueType && <SelectShowValueType
              timeType={filterValue?.timeType}
              value={selectType}
              onChange={(value) => { setSelectType(value) }}
              isShowDod
              isShowSdlw
            />}
          </div>
        ),
      }}
      onResizeEnd={(columns) => setColumns(columns)}
      isShowProgressBar
      loading={loading}
      isError={isError}
      downloadConfig={{
        onDownload: ({ isDownloadStatistics, metrics, dataType, fileFormat }) => {
          const params = getRequestParams(paging, sort, metrics, segValue, selectType, {
            // metrics: metrics,
            show_dod: dataType === "show_dod",
            show_sdlw: dataType === "show_sdlw",
            file_format: fileFormat,
            aggregates: isDownloadStatistics ? showStatistic : [],
            metrice_type: segValue,
          });

          // 扩展新api接口，兼容老模式
          let getTableList = downLoadFunnelPerformanceTable;
          let paramList = [params, LANGUAGE_MAP[getLocale()], pageType];

          if (api?.tableDownloadApi) {
            const tableDownloadApi = `${api.tableDownloadApi}?language=${LANGUAGE_MAP[getLocale()]}`;
            paramList = [tableDownloadApi, params];
            params.filters = formatFilterValues(params.filters);
            getTableList = downLoadApi;
          }
          return getTableList(...paramList);
        },
        metricOptions: stepOptions,
        showData: { valueOnly: true, showDod: true, showSdlw: true },
        defaultValue: stepValue,
        showModalData,
      }}
      frontLogicalFlag={frontLogicalFlag}
      limitCount={gameConfig?.tableConfig?.overLimitCount}
      barLimitCount={gameConfig?.tableConfig?.overLimitBarCount}
      barDownloadDisabled={gameConfig?.tableConfig?.barDownloadDisabled}
      overLimitBarTriggerKey={overLimitBarTriggerKey}
    />);
}
